import { BookOpen, FileText, Layers } from 'lucide-react'
import { Button, NavigationLink, ProductCoverPlaceholder, StatusBadge } from '@shared/design-system/ui'
import { resolvePublicMediaUrl } from '@shared/lib/media/public-media-url'
import type { MembersAreaListItem } from '../model/members-area.contract'
import {
  contentEditorActionLabel,
  contentKindLabels,
  formatFileSize,
  getCompletenessPresentation,
  memberPreviewPath,
} from '../model/members-area.presentation'
import {
  cardActions,
  cardTop,
  courseCard,
  courseGrid,
  courseMeta,
  courseName,
  cover,
  coverImage,
  metaItem,
} from './members-area.css'

function contentMeta(item: MembersAreaListItem) {
  if (item.kind === 'files') {
    return (
      <>
        <span className={metaItem}>
          <FileText size={14} aria-hidden />
          {item.fileCount} {item.fileCount === 1 ? 'arquivo' : 'arquivos'}
        </span>
        {item.totalSizeBytes > 0 ? (
          <span className={metaItem}>{formatFileSize(item.totalSizeBytes)}</span>
        ) : null}
      </>
    )
  }

  return (
    <>
      <span className={metaItem}>
        <Layers size={14} aria-hidden />
        {item.moduleCount} {item.moduleCount === 1 ? 'módulo' : 'módulos'}
      </span>
      <span className={metaItem}>
        <BookOpen size={14} aria-hidden />
        {item.lessonCount} {item.lessonCount === 1 ? 'aula' : 'aulas'}
      </span>
    </>
  )
}

export function ContentsPanel({
  items,
  onOpen,
}: {
  items: MembersAreaListItem[]
  onOpen: (item: MembersAreaListItem) => void
}) {
  return (
    <div className={courseGrid} data-testid="members-contents-grid">
      {items.map((item) => {
        const status = getCompletenessPresentation(item.completeness)
        const StatusIcon = status.icon
        return (
          <article
            key={item.productId}
            className={courseCard}
            data-testid={`members-content-${item.productId}`}
          >
            <div className={cover}>
              {item.coverImage ? (
                <img className={coverImage} src={resolvePublicMediaUrl(item.coverImage)} alt="" />
              ) : (
                <ProductCoverPlaceholder name={item.name} />
              )}
            </div>
            <div className={cardTop}>
              <div>
                <h3 className={courseName}>{item.name}</h3>
              </div>
              <StatusBadge
                tone={status.tone}
                label={status.label}
                icon={<StatusIcon size={13} />}
                size="sm"
              />
            </div>
            <div className={courseMeta}>
              <StatusBadge tone="informative" label={contentKindLabels[item.kind]} size="sm" />
              {contentMeta(item)}
            </div>
            <div className={cardActions}>
              <Button
                size="sm"
                onClick={() => onOpen(item)}
                data-testid={`members-content-edit-${item.productId}`}
              >
                {contentEditorActionLabel(item.completeness)}
              </Button>
              {item.status === 'PUBLISHED' ? (
                <NavigationLink
                  href={memberPreviewPath(item.productId)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ver como membro
                </NavigationLink>
              ) : null}
            </div>
          </article>
        )
      })}
    </div>
  )
}
